/**
 * Notifications Service
 * Handles fetching, marking as read, and managing user notifications
 */

import apiClient from './apiClient';

/**
 * Get all notifications for the current user
 * @param {Object} params - Query parameters
 * @param {boolean} params.unreadOnly - Only fetch unread notifications
 * @param {number} params.limit - Limit number of results
 * @returns {Promise<{data: Array, error: null}|{data: null, error: Error}>}
 */
export const getNotifications = async ({ unreadOnly = false, limit = 50 } = {}) => {
  try {
    const params = {};
    if (unreadOnly) {
      params.unreadOnly = true;
    }
    if (limit) {
      params.limit = limit;
    }

    const response = await apiClient.get('/notifications', params);
    
    // Handle different response formats
    const notifications = response?.data?.rows || response?.data || response?.rows || response || [];
    
    return {
      data: Array.isArray(notifications) ? notifications : [],
      error: null
    };
  } catch (error) {
    // Suppress 404, connection errors, and rate limit errors
    // Real-time notifications via Socket.io will still work
    const errorMessage = error?.message || error?.toString() || '';
    const isSuppressedError = error?.isConnectionError ||
                              error?.status === 429 ||
                              errorMessage.includes('404') ||
                              errorMessage.includes('429') ||
                              errorMessage.includes('Too many requests') ||
                              errorMessage.includes('Route not found') ||
                              errorMessage.includes('Backend server is not available') ||
                              errorMessage.includes('Failed to fetch') ||
                              errorMessage.includes('ERR_CONNECTION_REFUSED');
    
    if (isSuppressedError) {
      // Silently fail - don't log connection or rate limit errors
      // For rate limits, return empty array but don't treat as error
      return {
        data: [],
        error: null // Don't treat as error
      };
    }
    
    // Only log unexpected errors
    if (import.meta.env.DEV) {
      console.error('Error fetching notifications:', error);
    }
    return {
      data: [],
      error
    };
  }
};

/**
 * Get unread notification count
 * @returns {Promise<{data: number, error: null}|{data: 0, error: Error}>}
 */
export const getUnreadCount = async () => {
  try {
    const response = await apiClient.get('/notifications/unread/count');
    
    const count = response?.data?.count || response?.count || response?.data || 0;
    
    return {
      data: typeof count === 'number' ? count : 0,
      error: null
    };
  } catch (error) {
    // Suppress 404, connection errors, and rate limit errors
    // Real-time notifications via Socket.io will still work
    const errorMessage = error?.message || error?.toString() || '';
    const isSuppressedError = error?.isConnectionError ||
                              error?.status === 429 ||
                              errorMessage.includes('404') ||
                              errorMessage.includes('429') ||
                              errorMessage.includes('Too many requests') ||
                              errorMessage.includes('Route not found') ||
                              errorMessage.includes('Backend server is not available') ||
                              errorMessage.includes('Failed to fetch') ||
                              errorMessage.includes('ERR_CONNECTION_REFUSED');
    
    if (isSuppressedError) {
      // Silently fail - don't log connection or rate limit errors
      // For rate limits, return 0 but don't treat as error
      return {
        data: 0,
        error: null // Don't treat as error
      };
    }
    
    // Only log unexpected errors
    if (import.meta.env.DEV) {
      console.error('Error fetching unread count:', error);
    }
    return {
      data: 0,
      error
    };
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const markAsRead = async (notificationId) => {
  try {
    const response = await apiClient.patch(`/notifications/${notificationId}/read`);
    return {
      data: response?.data || response,
      error: null
    };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return {
      data: null,
      error
    };
  }
};

/**
 * Mark all notifications as read
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const markAllAsRead = async () => {
  try {
    const response = await apiClient.patch('/notifications/read-all');
    return {
      data: response?.data || response,
      error: null
    };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return {
      data: null,
      error
    };
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{success: boolean, error: null}|{success: false, error: Error}>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    await apiClient.delete(`/notifications/${notificationId}`);
    return {
      success: true,
      error: null
    };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return {
      success: false,
      error
    };
  }
};

/**
 * Create and send a notification to a user
 * @param {Object} notificationData - Notification data
 * @param {string} notificationData.user_id - User ID to send notification to
 * @param {string} notificationData.title - Notification title
 * @param {string} notificationData.message - Notification message
 * @param {string} [notificationData.type] - Notification type (info, success, warning, error)
 * @param {string} [notificationData.tenant_id] - Tenant ID (optional)
 * @param {Object} [notificationData.metadata] - Additional metadata (optional)
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const sendNotification = async (notificationData) => {
  try {
    const response = await apiClient.post('/notifications', notificationData);
    return {
      data: response?.data || response,
      error: null
    };
  } catch (error) {
    console.error('Error sending notification:', error);
    return {
      data: null,
      error
    };
  }
};

export default {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  sendNotification
};

